import { t } from '@lingui/core/macro'
import { z } from 'zod'

import { InspectionBinBoundsSchema } from './inspectionBinBounds'

const identity = { id: z.string().min(1).max(100), title: z.string() }
const Cell = z.union([
  z.string(),
  z.number().finite(),
  z.array(z.string()),
  z.null(),
])
export const ReportSelectionActionSchema = z.object({
  asset_source_id: z.string().min(1).max(100),
  label: z.string().min(1).max(120),
})
const TableSource = z.object({
  ...identity,
  kind: z.literal('table'),
  columns: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        unit: z.string().nullish(),
        maximum_fraction_digits: z.number().int().min(0).max(20).nullish(),
      })
    )
    .max(12),
  rows: z.array(z.record(z.string(), Cell)).max(100),
  row_actions: z
    .array(
      ReportSelectionActionSchema.extend({
        row_index: z.number().int().nonnegative(),
      })
    )
    .max(100)
    .nullish(),
  truncated: z.boolean(),
})
const ChartSource = z.object({
  ...identity,
  kind: z.literal('chart'),
  unit: z.string(),
  points: z
    .array(
      z.object({ label: z.string(), value: z.number().finite().nonnegative() })
    )
    .max(100),
  missing_count: z.number().int().nonnegative(),
  truncated: z.boolean(),
  notes: z.array(z.string()),
})
const AssetViewSource = z.object({
  ...identity,
  kind: z.literal('asset_view'),
  bin_bounds: InspectionBinBoundsSchema.nullish(),
  site_id: z.string(),
  unit_id: z.string(),
  component_id: z.string(),
  inspection_id: z.string(),
  dataset_id: z.string(),
  asset_model_id: z.string(),
  dataset_generation: z.number().int().nullish(),
  asset_model_generation: z.number().int().nullish(),
  column: z.string().nullish(),
  as_of_date: z.string().nullish(),
  layer: z.enum([
    'thickness',
    'corrosion_rate',
    'metal_loss',
    'remaining_life',
    'minimum_thickness',
    'remaining_corrosion_allowance',
  ]),
  mode: z.enum(['2D', '3D']),
  focus: z.enum(['whole', 'minimum', 'maximum']),
})
const ReportSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    ...identity,
    kind: z.literal('text'),
    paragraphs: z.array(z.string()),
  }),
  z.object({
    ...identity,
    kind: z.literal('callout'),
    tone: z.enum(['info', 'warning']),
    text: z.string(),
  }),
  z.object({
    ...identity,
    kind: z.literal('metrics'),
    items: z.array(
      z.object({
        label: z.string(),
        value: z.union([z.string(), z.number().finite(), z.null()]),
        unit: z.string().nullish(),
      })
    ),
  }),
  TableSource,
  ChartSource,
  AssetViewSource,
  z.object({
    ...identity,
    kind: z.literal('link'),
    href: z.string().regex(/^\/[^/\\\s]/),
  }),
])
export const ReportBlockSchema = z.object({
  source_id: z.string().min(1).max(100),
  title: z.string().max(120).nullish(),
  columns: z.array(z.string()).max(12).nullish(),
  layout: z.enum(['table', 'cards']).nullish(),
  chart_style: z.enum(['bar', 'column']).nullish(),
})
export const ReportViewSchema = z
  .object({
    version: z.literal(1),
    sources: z.array(ReportSourceSchema).max(30),
    layout: z.object({ blocks: z.array(ReportBlockSchema).min(1).max(20) }),
    required_before: z.array(z.string()),
    required_after: z.array(z.string()),
  })
  .superRefine((view, ctx) => {
    const sources = new Map(view.sources.map((source) => [source.id, source]))
    const fail = () =>
      ctx.addIssue({
        code: 'custom',
        message: t`Invalid report source reference or columns`,
      })
    if (sources.size !== view.sources.length) fail()
    for (const source of view.sources) {
      if (source.kind !== 'table') continue
      const rows = new Set<number>()
      for (const action of source.row_actions ?? []) {
        const target = sources.get(action.asset_source_id)
        if (
          target?.kind !== 'asset_view' ||
          action.row_index >= source.rows.length ||
          rows.has(action.row_index)
        )
          fail()
        rows.add(action.row_index)
      }
    }
    for (const id of [...view.required_before, ...view.required_after])
      if (!sources.has(id)) fail()
    for (const block of view.layout.blocks) {
      const source = sources.get(block.source_id)
      if (!source) {
        fail()
        continue
      }
      if (block.layout && source.kind !== 'table') fail()
      if (block.chart_style && source.kind !== 'chart') fail()
      if (
        block.columns &&
        (source.kind !== 'table' ||
          !block.columns.length ||
          new Set(block.columns).size !== block.columns.length ||
          block.columns.some(
            (id) => !source.columns.some((column) => column.id === id)
          ))
      )
        fail()
    }
  })
export type ReportView = z.infer<typeof ReportViewSchema>
export type ReportBlock = z.infer<typeof ReportBlockSchema>
export type ReportSource = z.infer<typeof ReportSourceSchema>
export type ReportTableSource = z.infer<typeof TableSource>
export type ReportChartSource = z.infer<typeof ChartSource>

export type ReportAssetSource = z.infer<typeof AssetViewSource>

export type ReportSelectionAction = z.infer<typeof ReportSelectionActionSchema>
